'use client';

import clsx from 'clsx';
import React from 'react';
import Icon, { IconName } from './Icon';

/**
 * DS `Toast` — measured from the Toasts master (385:4759).
 *
 * This component used to be inferred: when the library was first built the
 * Toasts frame in Figma rendered empty, so it was written from the prose in
 * the design doc and marked as guesswork (Q24). The frame is populated now,
 * and the master disagrees with the guess on every point that mattered —
 * four tones rather than three, tone carried by the icon rather than the
 * border, a surface-100 fill with a surface-200 hairline instead of a white
 * fill, a shadow where the guess said the system has none, and a second line
 * the guess did not have at all.
 *
 * The variant matrix is 4 tones x action/none x one-line/two-line, which
 * collapses to one component and two optional slots: `description` makes it
 * two-line, `action` adds the inline button.
 *
 * Geometry per the master: 560 wide, 16px horizontal padding, 8px vertical
 * on one line and 12px on two, 16px between the icon and the text, 4px
 * between the two lines, 20px icons throughout.
 */
export type ToastTone = 'success' | 'info' | 'alert' | 'error' | 'loading';

/**
 * Tone is the icon and its colour, nothing else — the surface stays neutral
 * in all four. Every colour in the master resolves to an existing accent
 * token exactly: #6F914C, #538CFF, #FF3900, #E21414.
 *
 * `loading` is not in the master. It draws the Button's spinner in the icon
 * slot, for work the user is waiting on, such as a purchase going through.
 */
const TONE: Record<Exclude<ToastTone, 'loading'>, { icon: IconName; className: string }> = {
    success: { icon: 'check', className: 'text-accent-green' },
    info: { icon: 'info', className: 'text-accent-blue' },
    alert: { icon: 'alert', className: 'text-accent-orange' },
    error: { icon: 'error', className: 'text-accent-red' }
};

export type ToastProps = {
    tone?: ToastTone;
    /** First line. Header/H4 in the master. */
    title: React.ReactNode;
    /** Optional second line — its presence is what makes the toast two-line. */
    description?: React.ReactNode;
    /** Optional inline action, drawn as a text button before the close. */
    action?: { label: React.ReactNode; onClick: () => void };
    /** Omit to render a toast that cannot be dismissed by hand. */
    onDismiss?: () => void;
    className?: string;
};

const Toast = ({ tone = 'info', title, description, action, onDismiss, className }: ToastProps) => {
    const twoLine = !!description;

    return (
        <div
            role="status"
            aria-live="polite"
            className={clsx(
                'pointer-events-auto flex w-[560px] max-w-[calc(100vw-32px)] gap-[8px]',
                'border border-surface-200 bg-surface-100 px-[16px] shadow-ds-200',
                twoLine ? 'items-start py-[12px]' : 'items-center py-[8px]',
                className
            )}
        >
            <div className={clsx('flex min-w-0 flex-1 gap-[16px]', twoLine ? 'items-start' : 'items-center')}>
                {tone === 'loading' ? (
                    <span
                        aria-hidden
                        className="size-[20px] shrink-0 animate-spin rounded-full border-2 border-ink-400 border-t-transparent"
                    />
                ) : (
                    <Icon name={TONE[tone].icon} size={20} className={TONE[tone].className} />
                )}

                <div className="flex min-w-0 flex-1 flex-col gap-[4px] break-words text-ink-400">
                    <span className="text-h4">{title}</span>
                    {description && <span className="text-body-300">{description}</span>}
                </div>

                {action && (
                    <button
                        type="button"
                        onClick={action.onClick}
                        /*
                         * The master sets this label in Manrope, which the
                         * project does not bind — the DS binds families
                         * through role vars and never names one in markup, so
                         * it inherits the body role at Body/400's metrics.
                         * Colour is the master's Text/200 (#595958), which our
                         * ink ramp still has no token for; the same literal is
                         * used on the PDP suggested-bid label and the search
                         * count. See the open question on Text/200 vs ink-200.
                         */
                        className="text-body-400 shrink-0 whitespace-nowrap text-[#595958] duration-press ease-ds-out hover:text-ink-400"
                    >
                        {action.label}
                    </button>
                )}
            </div>

            {onDismiss && (
                <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={onDismiss}
                    className="shrink-0 rounded-[8px] p-[8px] text-[#595958] duration-press ease-ds-out hover:text-ink-400"
                >
                    <Icon name="close" size={20} />
                </button>
            )}
        </div>
    );
};

export default Toast;
